import { authenticatedInstance } from '../index/index.service';
import type {
  PaymentlineView,
  SplitAndPayRequest,
  SplitAndPayTopResult,
  TenbisChargeRequest,
  TenbisChargeResultView,
  TenbisRefundRequest,
  TenbisRefundResultView,
  TipInputMode,
} from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import { getBonPrintContext } from '../../utils/pinned-bon-printer.util';

export type { SplitAndPayRequest, SplitAndPayTender, SplitAndPayTopResult } from 'shared-models/browser';

export type { PaymentlineStatus, PaymentlineView, TipInputMode } from 'shared-models/browser';

export type SplitPaymentlinePayload =
  | { mode: 'equal'; count: number }
  | { mode: 'custom'; amount: number }
  | { mode: 'by_product'; items: Array<{ orderlineId: number; quantity: number }> };

// Set a tip with mode+value, or clear it with both null.
export type TipPayload = { tipInputMode: TipInputMode; tipInputValue: number } | { tipInputMode: null; tipInputValue: null };

export type ResolveOverpaymentPayload = {
  paymentlineId?: number;
  cashDrawerPrinterId?: number;
};

export type CashOverageAction = 'return_change' | 'add_to_tip';

export type CashDrawerOption = { id: number; name: string };

// Thrown when a cash close hits a multi-drawer tenant with no drawer chosen yet (Rule 41) — carries the options.
export class CashDrawerSelectionRequiredError extends Error {
  constructor(public readonly drawers: CashDrawerOption[]) {
    super('CASH_DRAWER_SELECTION_REQUIRED');
    this.name = 'CashDrawerSelectionRequiredError';
  }
}

// Maps a 409 drawer-selection payload to the typed error; null for any other failure.
export const asDrawerSelectionError = (error: unknown): CashDrawerSelectionRequiredError | null => {
  const data = (error as { response?: { data?: { code?: string; drawers?: CashDrawerOption[] } } }).response?.data;
  if (data?.code === 'CASH_DRAWER_SELECTION_REQUIRED') {
    return new CashDrawerSelectionRequiredError(data.drawers ?? []);
  }
  return null;
};

class PaymentsService {
  // Summary: Lists paymentlines for a tenant order so the POS payment modal can render the current 'new'/'done' state.
  // Details: docs/function-library/web/services.md#payments-service
  async listForOrder(
    tenantId: string,
    orderId: number,
    employeeSession: string,
  ): Promise<PaymentlineView[]> {
    try {
      const response = await authenticatedInstance.get<PaymentlineView[]>(
        `/payments/tenant/${tenantId}/order/${orderId}`,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Idempotent ensure of a single 'new' paymentline for an order.
  // Details: docs/function-library/web/services.md#payments-service
  async createForOrder(
    tenantId: string,
    orderId: number,
    employeeSession: string,
    options?: { tipOnly?: true },
  ): Promise<PaymentlineView> {
    try {
      const response = await authenticatedInstance.post<PaymentlineView>(
        `/payments/tenant/${tenantId}/order/${orderId}`,
        options?.tipOnly ? { tipOnly: true } : {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Marks a 'new' paymentline as 'done' so the cashier can then close the order.
  // Details: docs/function-library/web/services.md#payments-service
  async markDone(
    tenantId: string,
    paymentlineId: number,
    employeeSession: string,
    cashDrawerPrinterId?: number,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/${paymentlineId}/done`,
        { ...(cashDrawerPrinterId ? { cashDrawerPrinterId } : {}), ...getBonPrintContext() },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      const drawerError = asDrawerSelectionError(error);
      if (drawerError) {
        throw drawerError;
      }
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Synchronous Tenbis (10bis) card charge; a decline surfaces the Hebrew ErrorDesc as the message.
  async chargeTenbis(
    tenantId: string,
    paymentlineId: number,
    payload: TenbisChargeRequest,
    employeeSession: string,
  ): Promise<TenbisChargeResultView> {
    try {
      const response = await authenticatedInstance.post<TenbisChargeResultView>(
        `/payments/tenant/${tenantId}/paymentlines/${paymentlineId}/tenbis-charge`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async refundTenbis(
    tenantId: string,
    paymentlineId: number,
    payload: TenbisRefundRequest,
    employeeSession: string,
  ): Promise<TenbisRefundResultView> {
    try {
      const response = await authenticatedInstance.post<TenbisRefundResultView>(
        `/payments/tenant/${tenantId}/paymentlines/${paymentlineId}/tenbis-refund`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async settleHouseAccount(
    tenantId: string,
    paymentlineId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/${paymentlineId}/house-account`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async settleCashOverage(
    tenantId: string,
    paymentlineId: number,
    payload: { tenderedAmount: number; action: CashOverageAction; cashDrawerPrinterId?: number },
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/${paymentlineId}/cash-overage`,
        { ...payload, ...getBonPrintContext() },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      const drawerError = asDrawerSelectionError(error);
      if (drawerError) {
        throw drawerError;
      }
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  async resolveOverpaymentAndClose(
    tenantId: string,
    orderId: number,
    payload: ResolveOverpaymentPayload,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/order/${orderId}/resolve-overpayment`,
        { ...payload, ...getBonPrintContext() },
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      const drawerError = asDrawerSelectionError(error);
      if (drawerError) {
        throw drawerError;
      }
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Splits a 'new' paymentline into child lines server-side (equal, custom, or by selected items). Backend supersedes the source line and fans out the new lines via SSE.
  // Details: docs/function-library/web/services.md#payments-service
  async splitPaymentline(
    tenantId: string,
    orderId: number,
    paymentlineId: number,
    payload: SplitPaymentlinePayload,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/order/${orderId}/line/${paymentlineId}/split`,
        payload,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Odoo partial tender — split a line by total T and pay the paid child via the tender.
  async splitAndPay(
    tenantId: string,
    orderId: number,
    paymentlineId: number,
    payload: SplitAndPayRequest,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/payments/tenant/${tenantId}/order/${orderId}/line/${paymentlineId}/split-and-pay`,
        { ...payload, ...getBonPrintContext() },
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      const drawerError = asDrawerSelectionError(error);
      if (drawerError) {
        throw drawerError;
      }
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: TOP partial — split by total T; return the paid child's BASE for the device to charge.
  async splitForTopTender(
    tenantId: string,
    orderId: number,
    paymentlineId: number,
    amount: number,
    employeeSession: string,
  ): Promise<SplitAndPayTopResult> {
    try {
      const response = await authenticatedInstance.post<SplitAndPayTopResult>(
        `/payments/tenant/${tenantId}/order/${orderId}/line/${paymentlineId}/split-and-pay`,
        { tender: 'top', amount, ...getBonPrintContext() },
        { headers: { 'x-employee-session': employeeSession } },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Set/clear the order's global tip (mode+value stored; SSE order.upserted carries the change).
  // Details: docs/function-library/web/services.md#payments-service
  async setOrderTip(
    tenantId: string,
    orderId: number,
    payload: TipPayload,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/order/${orderId}/tip`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Set/clear a payment line's own tip (SSE paymentline.created carries the change).
  // Details: docs/function-library/web/services.md#payments-service
  async setPaymentlineTip(
    tenantId: string,
    paymentlineId: number,
    payload: TipPayload,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/line/${paymentlineId}/tip`,
        payload,
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Undo a finalized cash payment — the line returns to 'new' and the order reopens (SSE fans out).
  // Details: docs/function-library/web/services.md#payments-service
  async revertCashPayment(
    tenantId: string,
    paymentlineId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/${paymentlineId}/revert-cash`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Void a paid line on an open order (SSE paymentline.created carries the canceled line).
  // Details: docs/function-library/web/services.md#payments-service
  async cancelPaymentline(
    tenantId: string,
    paymentlineId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/line/${paymentlineId}/cancel`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Atomic "אפס" — clears the order tip + collapses open lines in one call (SSE fans out the changes).
  // Details: docs/function-library/web/services.md#payments-service
  async resetOrderTipAndLines(
    tenantId: string,
    orderId: number,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/payments/tenant/${tenantId}/order/${orderId}/reset-lines`,
        {},
        { headers: { 'x-employee-session': employeeSession } },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const paymentsService = new PaymentsService();



